/**
 * 路由配置文件
 * - 使用 React Router v6 createBrowserRouter 数据路由
 * - 所有页面通过 React.lazy 实现按需加载（代码分割）
 * - MainLayout 作为根布局，包含 Header/Content/Footer
 * - AuthGuard 组件保护需要登录的路由
 * - 管理员路由额外校验 roles: ['admin']
 */

import {Button, Result, Spin} from 'antd'
import MainLayout from '@/components/Layout'
import React, {Suspense, lazy} from 'react'
import AuthGuard from '@/components/AuthGuard'
import {createBrowserRouter, isRouteErrorResponse, useRouteError} from 'react-router-dom'

// ====== 懒加载页面组件 ======
const Cart = lazy(() => import('@/views/Cart'))
const Home = lazy(() => import('@/views/Home'))
const Chat = lazy(() => import('@/views/Chat'))
const Admin = lazy(() => import('@/views/Admin'))
const Orders = lazy(() => import('@/views/Orders'))
const CatsList = lazy(() => import('@/views/Cats'))
const Profile = lazy(() => import('@/views/Profile'))
const Login = lazy(() => import('@/views/Auth/Login'))
const NotFound = lazy(() => import('@/views/NotFound'))
const Checkout = lazy(() => import('@/views/Checkout'))
const Products = lazy(() => import('@/views/Products'))
const Listings = lazy(() => import('@/views/Listings'))
const Community = lazy(() => import('@/views/Community'))
const CatDetail = lazy(() => import('@/views/Cats/Detail'))
const Register = lazy(() => import('@/views/Auth/Register'))
const OrderDetail = lazy(() => import('@/views/Orders/Detail'))
const PostDetail = lazy(() => import('@/views/Community/Detail'))
const Notifications = lazy(() => import('@/views/Notifications'))
const ProductDetail = lazy(() => import('@/views/Products/Detail'))
const ListingDetail = lazy(() => import('@/views/Listings/Detail'))
const OAuthCallback = lazy(() => import('@/views/Auth/OAuthCallback'))

/** 全局加载占位组件 */
const Loading = () => (
  <div
    style={{
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      height: '60vh'
    }}
  >
    <Spin size='large' tip='加载中...' />
  </div>
)

/**
 * 路由级错误兜底。
 *
 * React Router 没有 errorElement 时，会展示默认英文错误页。
 * 管理后台这种排查页面更需要稳定：即使某个 Tab 组件出错，也应该给用户一个中文提示和刷新入口。
 */
const RouteErrorFallback = () => {
  const error = useRouteError()
  const title = isRouteErrorResponse(error) ? `${error.status} ${error.statusText}` : '页面出错了'
  const message = error instanceof Error ? error.message : '页面渲染时遇到异常，请刷新后重试'

  return (
    <Result
      status='error'
      title={title}
      subTitle={message}
      extra={
        <Button type='primary' onClick={() => window.location.reload()}>
          刷新页面
        </Button>
      }
    />
  )
}

/** 包裹 Suspense 实现懒加载 fallback */
const withSuspense = (element: React.ReactNode) => {
  return <Suspense fallback={<Loading />}>{element}</Suspense>
}

/** 包裹 AuthGuard + Suspense，保护需要登录的路由 */
const withAuth = (element: React.ReactNode, roles?: string[]) => (
  <AuthGuard roles={roles}>{withSuspense(element)}</AuthGuard>
)

export const router = createBrowserRouter([
  {
    path: '/',
    element: <MainLayout />,
    errorElement: <RouteErrorFallback />,
    children: [
      // 公开页面
      {index: true, element: withSuspense(<Home />)},
      {path: 'cats', element: withSuspense(<CatsList />)},
      {path: 'cats/:id', element: withSuspense(<CatDetail />)},
      {path: 'products', element: withSuspense(<Products />)},
      {path: 'products/:id', element: withSuspense(<ProductDetail />)},
      {path: 'listings', element: withSuspense(<Listings />)},
      {path: 'listings/:id', element: withSuspense(<ListingDetail />)},
      {path: 'community', element: withSuspense(<Community />)},
      {path: 'community/:id', element: withSuspense(<PostDetail />)},
      // 需要登录的页面
      {path: 'profile', element: withAuth(<Profile />)},
      {path: 'cart', element: withAuth(<Cart />)},
      {path: 'checkout', element: withAuth(<Checkout />)},
      {path: 'orders', element: withAuth(<Orders />)},
      {path: 'orders/:id', element: withAuth(<OrderDetail />)},
      {path: 'notifications', element: withAuth(<Notifications />)},
      {path: 'chat', element: withAuth(<Chat />)},
      // 管理员页面（需要 admin 角色）
      {path: 'admin/*', element: withAuth(<Admin />, ['admin'])}
    ]
  },
  // 认证页面（独立布局，不使用 MainLayout）
  {path: '/auth/login', element: withSuspense(<Login />)},
  {path: '/auth/register', element: withSuspense(<Register />)},
  {path: '/auth/github/callback', element: withSuspense(<OAuthCallback />)},
  // 404 兜底
  {path: '*', element: withSuspense(<NotFound />)}
])
